/**
 * شاشة «فريقي» — الطابور غير الموزّع · مين شغال على إيه · متأخرات الفريق
 * · نبض النهارده. الماركب والقيم من design/Team.dc.html.
 *
 * 🔴 البلاغان اللي الشاشة دي اتعدّلت عشانهم:
 *    ١. زرار «إعادة توزيع» كان بيوعد «بتنقل لعضو أقل حِملًا» وهو واقف على
 *       صف صاحبه **هو** الأقل حِملًا — وعد كذب.
 *    ٢. وبعد ما اتصلح، الزرار كان **بيتعطّل** لو مفيش حد أقل حِملًا، والمنسق
 *       عنده أسباب تانية للنقل (إجازة · لغة · خبرة بالأسرة). دلوقتي **هو
 *       اللي بيختار الشخص** من قايمة فيها حِمل كل عضو، والأخف مختار
 *       افتراضيًا. الوجهة لسه مكتوبة قبل الضغطة — مفيش وجهة مخفية.
 *
 * ⚠️ الطابور فيه قنوات مالهاش مدخل آلي (واتساب · تعديل موعد من البورتال)
 *    ⇒ بتتعلّم «إدخال يدوي» بصراحة. ⛔ ممنوع نوحي إن السيستم بيسحبها لوحده.
 *
 * ⛔ فعل أساسي كحلي **واحد** في الشاشة: استلام أول عنصر في الطابور.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Shell } from "@/components/Shell";
import { Icon } from "@/components/Icon";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  NoResultsState,
  OfflineState,
} from "@/components/states/ScreenStates";
import { useSession } from "@/lib/session";
import { useRouter } from "@/lib/router";
import { Recurrences } from "@/components/Recurrences";
import { fmtNum } from "@/lib/format";
import { errorMessageAr } from "@/api/client";
import {
  claimTask,
  loadTeamBoard,
  reassignCandidates,
  reassignTask,
  suggestReassignTarget,
  type Loaded,
  type QueueItem,
  type TeamBoard,
  type TeamMember,
  type TeamTask,
  type Workload,
} from "@/api/team";
import { fetchFunnel, type Funnel } from "@/api/leads";
import "@/styles/registry.css";

/** 🔁 مسار تبويب المهام المتكررة — نفس الشاشة، محتوى تاني */
const RECURRING_PATH = "/team/recurring";

/**
 * ⛔ الصلاحية اللي المسارات بتطلبها فعلًا (`routers/recurrences.py`).
 * ⚠️ الإخفاء هنا **راحة مش أمان**: السيرفر بيرفض من غيرها برضه. بنخفي
 *    التبويب عشان ما نوقّعش العضو على «مش من صلاحياتك» في شاشة هو
 *    مالوش دعوة بيها أصلًا.
 */
const RECURRENCE_PERM = "task.create_for_others";

/** سبب إجباري في السيستم — بيتسجّل في السجل مع كل إعادة توزيع */
function reassignReason(target: TeamMember): string {
  return `إعادة توزيع من شاشة فريقي — المنسق اختار ${target.name_ar}`;
}

type FunnelState =
  | { kind: "loading" }
  | { kind: "ready"; value: Funnel }
  | { kind: "error"; messageAr: string };

export function Team() {
  const { me } = useSession();
  const { path, navigate } = useRouter();
  const dept = me?.user.department ?? null;
  const deptLabel = me?.user.department_name_ar ?? null;
  // ⚠️ مفيش استثناء صريح للمراقب هنا — على عكس `canReassign` تحت — لأن
  //    `effective_permissions` في السيستم بتقصّ صلاحيات المراقب على أفعال
  //    القراءة بس، و`task.create_for_others` مش منها ⇒ عمرها ما بتوصله.
  const canManageRecurrences = (me?.permissions ?? []).includes(RECURRENCE_PERM);
  // ⚠️ اللي مالوش صلاحية بيتعرض عليه تبويب الفريق زي ما هو — مش شاشة رفض،
  //    لأنه أصلًا ما شافش زرار يوصّله هنا.
  const onRecurring = path.startsWith(RECURRING_PATH) && canManageRecurrences;
  // ⛔ المراقب مستثنى صراحةً حتى لو المفتاح موجود — «حسابك للاطلاع بس».
  //    وده تحسين تجربة بس: المنع الحقيقي في `require_perm` + `scope_filter`.
  const canReassign =
    (me?.permissions ?? []).includes("task.reassign") && me?.user.role !== "observer";

  const [board, setBoard] = useState<TeamBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState<string | null>(null);
  const [funnel, setFunnel] = useState<FunnelState>({ kind: "loading" });
  const [query, setQuery] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [focusMember, setFocusMember] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFatal(null);
    try {
      setBoard(await loadTeamBoard(dept));
    } catch (err) {
      // ⛔ ممنوع الفشل الصامت — السبب بيتسجّل وبيتعرض
      console.error("[team] تحميل الشاشة فشل", err);
      setFatal(errorMessageAr(err));
    } finally {
      setLoading(false);
    }
  }, [dept]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let alive = true;
    setFunnel({ kind: "loading" });
    fetchFunnel("day").then(
      (value) => {
        if (alive) setFunnel({ kind: "ready", value });
      },
      (err: unknown) => {
        console.error("[team] «نبض النهارده» فشل", err);
        if (alive) setFunnel({ kind: "error", messageAr: errorMessageAr(err) });
      },
    );
    return () => {
      alive = false;
    };
  }, []);

  useEffect(
    () => () => {
      if (toastTimer.current) window.clearTimeout(toastTimer.current);
    },
    [],
  );

  function showToast(text: string) {
    setToast(text);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 6000);
  }

  function noteRowError(id: string, messageAr: string) {
    setRowErrors((prev) => ({ ...prev, [id]: messageAr }));
  }

  function clearRowError(id: string) {
    setRowErrors((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  /** استلام — قفل ضد الدوس المزدوج + رسالة صريحة نجاح أو فشل */
  async function onClaim(task: QueueItem) {
    if (busyId) return;
    setBusyId(task.id);
    clearRowError(task.id);
    try {
      await claimTask(task.id);
      showToast(`استلمت «${task.title_ar}» — بقت مسؤوليتك`);
      await load();
    } catch (err) {
      console.error(`[team] استلام المهمة ${task.id} فشل`, err);
      noteRowError(task.id, errorMessageAr(err));
    } finally {
      setBusyId(null);
    }
  }

  /** إعادة توزيع — **المنسق بيختار الشخص**، والسيستم ما بيخمّنش الوجهة */
  async function onReassign(task: TeamTask, target: TeamMember) {
    if (busyId) return;
    setBusyId(task.id);
    clearRowError(task.id);
    try {
      await reassignTask(task.id, target.id, reassignReason(target), task.version);
      showToast(`«${task.title_ar}» اتنقلت لـ${target.name_ar}`);
      await load();
    } catch (err) {
      console.error(`[team] إعادة توزيع المهمة ${task.id} فشلت`, err);
      noteRowError(task.id, errorMessageAr(err));
    } finally {
      setBusyId(null);
    }
  }

  const q = query.trim();
  const queueAll = board?.queue.ok ? board.queue.value : [];
  const overdueAll = board?.overdue.ok ? board.overdue.value : [];
  const workload: Workload | null = board?.workload.ok ? board.workload.value : null;
  const members = workload?.members ?? [];

  const queue = useMemo(
    () => (q ? queueAll.filter((t) => matches(q, t.title_ar, t.body_ar, t.entity_label_ar)) : queueAll),
    [queueAll, q],
  );
  const visibleMembers = useMemo(
    () => (q ? members.filter((m) => matches(q, m.name_ar)) : members),
    [members, q],
  );
  const overdue = useMemo(() => {
    const byMember = focusMember
      ? overdueAll.filter((t) => t.assignee_id === focusMember)
      : overdueAll;
    return q
      ? byMember.filter((t) => matches(q, t.title_ar, t.body_ar, t.entity_label_ar))
      : byMember;
  }, [overdueAll, q, focusMember]);

  const nothingForQuery =
    q.length > 0 && queue.length === 0 && visibleMembers.length === 0 && overdue.length === 0;

  const focusName = members.find((m) => m.id === focusMember)?.name_ar ?? null;
  // ⛔ فعل أساسي كحلي واحد بس في الشاشة: أول عنصر في الطابور
  const primaryClaimId = queue[0]?.id ?? null;

  // ⛔ ممنوع رقم مخترع في السطر ده: القسم اللي فشل بيتقال «مش متاح» مش «0»
  const crumb = (
    <>
      {board?.workload.ok ? (
        <>
          <span className="num">{fmtNum(members.length)}</span> أعضاء
        </>
      ) : (
        <>الأعضاء مش متاحين</>
      )}{" "}
      ·{" "}
      {board?.overdue.ok ? (
        <>
          <span className="num">{fmtNum(overdueAll.length)}</span> متأخرات
        </>
      ) : (
        <>المتأخرات مش متاحة</>
      )}{" "}
      · التوزيع بالمهام
    </>
  );

  return (
    <Shell
      title={
        onRecurring
          ? "فريقي — المهام المتكررة"
          : deptLabel
            ? `فريقي — ${deptLabel}`
            : "فريقي"
      }
      crumb={onRecurring ? <>القالب بيولّد نسخة كل يوم مستحق</> : crumb}
      search={{
        value: query,
        onChange: setQuery,
        placeholder: onRecurring ? "ابحث باسم المهمة…" : "ابحث باسم الأسرة أو العضو…",
      }}
    >
      {/* 🔁 التبويبان — بيظهروا للمنسق والمدير بس */}
      {canManageRecurrences ? (
        <div className="reg-tabs" style={{ marginBottom: 16 }}>
          <button
            type="button"
            className={`btn sm ${onRecurring ? "ghost" : ""}`}
            aria-pressed={!onRecurring}
            onClick={() => navigate("/team")}
          >
            الفريق
          </button>
          <button
            type="button"
            className={`btn sm ${onRecurring ? "" : "ghost"}`}
            aria-pressed={onRecurring}
            onClick={() => navigate(RECURRING_PATH)}
          >
            المهام المتكررة
          </button>
        </div>
      ) : null}

      {onRecurring ? <Recurrences query={query} myDept={dept} /> : null}

      {/* أول تحميل بس بيقفل الشاشة — التحديث بعد أي فعل بيسيب المحتوى مكانه */}
      {!onRecurring && loading && board === null ? <LoadingState rows={5} /> : null}

      {!onRecurring && fatal ? (
        <ErrorState messageAr={fatal} onRetry={() => void load()} />
      ) : null}

      {!onRecurring && board !== null && nothingForQuery ? (
        <NoResultsState query={q} onClear={() => setQuery("")} />
      ) : null}

      {!onRecurring && board !== null && !nothingForQuery ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) clamp(300px, 28%, 360px)",
            gap: 16,
            alignItems: "start",
            minHeight: 0,
          }}
        >
          {/* ═══════ العمود الرئيسي ═══════ */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
            {/* (1) طابور غير موزّع */}
            <div
              className="card"
              style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
            >
              <div className="sec-h">
                <span className="sec-title">
                  طابور غير موزّع (<span className="num">{fmtNum(queue.length)}</span>)
                </span>
                <span className="sec-note">الاستلام بيثبّت المسؤولية</span>
                <span className="t-meta" style={{ marginRight: "auto" }}>
                  <LoadNow members={members} available={board?.workload.ok === true} />
                </span>
              </div>

              <SectionFailure section={board?.queue} onRetry={() => void load()} />

              {board?.queue.ok && queue.length === 0 ? (
                <EmptyState
                  title={q ? "مفيش عنصر في الطابور بالبحث ده" : "الطابور فاضي"}
                  hint={q ? "جرّب اسم تاني" : "كل الطلبات اتسلّمت — تمام كده"}
                />
              ) : null}

              {queue.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {queue.map((task, i) => (
                    <div
                      key={task.id}
                      className="row-link"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "132px minmax(0, 1fr) auto",
                        alignItems: "center",
                        gap: 12,
                        ...(i > 0
                          ? { borderTop: "1px solid var(--line)", paddingTop: 16 }
                          : {}),
                      }}
                    >
                      <span className="chip ghost" style={{ justifySelf: "start" }}>
                        <span className="bullet" />
                        {task.source_label_ar || "بلا مصدر"}
                      </span>

                      <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
                          <span className="t-body clip">{task.title_ar}</span>
                          {task.manual_entry ? (
                            <span className="src" title="القناة دي مالهاش مدخل آلي — الفريق بيسجّلها بإيده">
                              إدخال يدوي
                            </span>
                          ) : null}
                          {task.will_escalate ? (
                            <span className="chip soon">
                              <span className="bullet" />
                              هيتصعّد للمنسقة
                            </span>
                          ) : null}
                        </div>
                        <span className="t-desc clip">
                          {[task.waiting_label_ar, task.entity_label_ar, task.body_ar]
                            .filter((s) => s.length > 0)
                            .join(" · ")}
                        </span>
                        <RowError message={rowErrors[task.id]} />
                      </div>

                      <button
                        type="button"
                        className={`btn sm ${task.id === primaryClaimId ? "primary" : "ghost"}${
                          busyId === task.id ? " is-busy" : ""
                        }`}
                        disabled={busyId !== null}
                        onClick={() => void onClaim(task)}
                      >
                        {busyId === task.id ? "بيتسلّم…" : "استلام"}
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}

              <div
                className="t-meta"
                style={{ borderTop: "1px solid var(--line)", paddingTop: 16, lineHeight: 1.7 }}
              >
                العنصر اللي بيعدّي مهلة الاستلام بيتصعّد للمنسقة تلقائيًا.
                <br />
                واتساب وطلبات تعديل المواعيد من البورتال مالهاش مدخل آلي — بتتسجّل بإيد الفريق،
                والسيستم ما بيسحبهاش لوحده.
              </div>
            </div>

            {/* (2) مين شغال على إيه */}
            <div
              className="card"
              style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
            >
              <div className="sec-h">
                <span className="sec-title">مين شغال على إيه</span>
                <span className="sec-note">الأرقام كلها مهام</span>
              </div>

              <SectionFailure section={board?.workload} onRetry={() => void load()} />

              {workload?.partial ? (
                <div className="chip soon" style={{ alignSelf: "flex-start" }}>
                  <span className="bullet" />
                  الأرقام محسوبة على أول <span className="num">
                    {fmtNum(workload.scanned)}
                  </span>{" "}
                  مهمة بس
                </div>
              ) : null}

              {board?.workload.ok && visibleMembers.length === 0 ? (
                <EmptyState
                  title={q ? "مفيش عضو بالاسم ده" : "مفيش أعضاء في الفريق"}
                  hint={q ? "جرّب اسم تاني" : "الإدارة بتضيف الأعضاء من شاشة المستخدمين"}
                />
              ) : null}

              {visibleMembers.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th style={{ borderRadius: "0 8px 8px 0" }}>العضو</th>
                        <th>مهام مفتوحة</th>
                        <th>متأخرة</th>
                        <th>اتقفل النهارده</th>
                        <th>آخر نشاط</th>
                        <th style={{ borderRadius: "8px 0 0 8px" }}>إجراء</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleMembers.map((m) => (
                        <tr key={m.id} className="row-link">
                          <td className="t-body">
                            {m.name_ar}
                            {m.can_receive ? null : (
                              <span className="src" style={{ marginRight: 8 }}>
                                مش بيستقبل شغل
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="num">{fmtNum(m.open_tasks)}</span>
                          </td>
                          <td>
                            {m.overdue_tasks > 0 ? (
                              <span className="chip late">
                                <span className="bullet" />
                                <span className="num">{fmtNum(m.overdue_tasks)}</span>
                              </span>
                            ) : (
                              <span className="t-desc num">{fmtNum(0)}</span>
                            )}
                          </td>
                          <td className="t-desc">{m.closed_today === null ? "—" : fmtNum(m.closed_today)}</td>
                          <td className="t-desc">{m.last_activity_at ?? "—"}</td>
                          <td>
                            <button
                              type="button"
                              className={`btn sm ${m.overdue_tasks > 0 ? "ghost" : "is-off"}`}
                              disabled={m.overdue_tasks === 0}
                              aria-pressed={focusMember === m.id}
                              onClick={() =>
                                setFocusMember((cur) => (cur === m.id ? null : m.id))
                              }
                            >
                              {focusMember === m.id ? "إلغاء التركيز" : "عرض المتأخرات"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}

              <div className="t-meta" style={{ borderTop: "1px solid var(--line)", paddingTop: 16 }}>
                «اتقفل النهارده» و«آخر نشاط» لسه ما بيرجعوش من السيستم — بيتعرضوا «—» بدل رقم
                مخترع.
              </div>
            </div>
          </div>

          {/* ═══════ العمود الجانبي ═══════ */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
            {/* (3) متأخرات الفريق */}
            <div
              className="card"
              style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div className="sec-h">
                  <span className="sec-title">
                    متأخرات الفريق (<span className="num">{fmtNum(overdue.length)}</span>)
                  </span>
                </div>
                <span className="sec-note">
                  إنت اللي بتختار الوجهة — والرقم جنب كل اسم هو حِمله دلوقتي.
                </span>
                {focusName ? (
                  <button
                    type="button"
                    className="link"
                    style={{ textAlign: "right" }}
                    onClick={() => setFocusMember(null)}
                  >
                    بيعرض متأخرات {focusName} — عرض الكل
                  </button>
                ) : null}
              </div>

              <SectionFailure section={board?.overdue} onRetry={() => void load()} />

              {board?.overdue.ok && overdue.length === 0 ? (
                <div className="t-desc">مفيش متأخرات — تمام كده</div>
              ) : null}

              {overdue.map((task, i) => (
                <OverdueRow
                  key={task.id}
                  task={task}
                  first={i === 0}
                  members={members}
                  workloadKnown={board?.workload.ok === true}
                  canReassign={canReassign}
                  busy={busyId === task.id}
                  locked={busyId !== null}
                  error={rowErrors[task.id]}
                  onReassign={(target) => void onReassign(task, target)}
                />
              ))}
            </div>

            {/* (4) نبض النهارده */}
            <div
              className="card"
              style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}
            >
              <div className="sec-h">
                <span className="sec-title">نبض النهارده</span>
              </div>
              {funnel.kind === "loading" ? <LoadingState rows={3} /> : null}
              {funnel.kind === "error" ? (
                <div className="t-desc">مقدرناش نجيب نبض النهارده — {funnel.messageAr}</div>
              ) : null}
              {funnel.kind === "ready" ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <PulseRow n={funnel.value.arrived} label="ليدات جديدة" first />
                  <PulseRow n={funnel.value.contacted} label="تم التواصل" />
                  <PulseRow n={funnel.value.evaluation_booked} label="حجز تقييم" />
                  <PulseRow n={funnel.value.completed} label="اكتملوا" />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {toast ? (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            right: "50%",
            transform: "translateX(50%)",
            zIndex: 40,
          }}
        >
          <div className="toast" role="status">
            <Icon name="check" size={14} stroke="#FFFFFF" strokeWidth={2.2} />
            {toast}
          </div>
        </div>
      ) : null}
    </Shell>
  );
}

/* ─────────────────────── مكوّنات الشاشة ─────────────────────── */

/** «الحِمل الآن: مشرفة الدعم أ 7 · مشرفة الدعم ج 5» — أو الصمت لو الحِمل نفسه مش متاح */
function LoadNow({ members, available }: { members: TeamMember[]; available: boolean }) {
  if (!available) return <>الحِمل مش متاح دلوقتي</>;
  if (members.length === 0) return <>مفيش أعضاء</>;
  return (
    <>
      الحِمل الآن:{" "}
      {members.slice(0, 4).map((m, i) => (
        <span key={m.id}>
          {i > 0 ? " · " : null}
          {m.name_ar} <span className="num">{fmtNum(m.open_tasks)}</span>
        </span>
      ))}
    </>
  );
}

function PulseRow({ n, label, first }: { n: number; label: string; first?: boolean }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 8,
        ...(first ? {} : { borderTop: "1px solid var(--line)", paddingTop: 16 }),
      }}
    >
      <span className="t-body num">{fmtNum(n)}</span>
      <span className="t-desc">{label}</span>
    </div>
  );
}

/**
 * صف متأخرة في «متأخرات الفريق» — ومعاه **اختيار الوجهة**.
 *
 * 🔴 قبل كده الزرار كان بيحسب الوجهة لوحده وبيتعطّل لو مفيش حد أقل حِملًا،
 *    فالمدير ماكانش يقدر ينقل مهمة لسبب غير الحِمل (إجازة · لغة · خبرة
 *    بالأسرة). دلوقتي القايمة بتعرض كل عضو نشط ومعاه حِمله، والأخف بيبقى
 *    مختارًا افتراضيًا — الوجهة **مكتوبة قبل الضغطة** زي ما كانت.
 */
function OverdueRow({
  task,
  first,
  members,
  workloadKnown,
  canReassign,
  busy,
  locked,
  error,
  onReassign,
}: {
  task: TeamTask;
  first: boolean;
  members: TeamMember[];
  workloadKnown: boolean;
  canReassign: boolean;
  busy: boolean;
  locked: boolean;
  error: string | undefined;
  onReassign: (target: TeamMember) => void;
}) {
  const candidates = useMemo(
    () => reassignCandidates(members, task.assignee_id),
    [members, task.assignee_id],
  );
  const suggested = useMemo(
    () => suggestReassignTarget(members, task.assignee_id),
    [members, task.assignee_id],
  );
  const [picked, setPicked] = useState<string | null>(null);
  const targetId = picked ?? suggested?.id ?? candidates[0]?.id ?? null;
  const target = candidates.find((m) => m.id === targetId) ?? null;
  const holder = members.find((m) => m.id === task.assignee_id)?.name_ar ?? null;
  const selectId = `reassign-${task.id}`;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        minWidth: 0,
        ...(first ? {} : { borderTop: "1px solid var(--line)", paddingTop: 16 }),
      }}
    >
      <span className="t-body clip">{task.title_ar}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span className="chip late">
          <span className="bullet" />
          {latinDigits(task.sla_label_ar ?? "متأخرة")}
        </span>
        {holder ? <span className="t-desc">عند {holder}</span> : null}
      </div>

      {!canReassign ? null : !workloadKnown ? (
        // ⛔ ممنوع نوعد بوجهة مش معروفة — القايمة نفسها مش موجودة دلوقتي
        <span className="t-meta">الفريق مش متاح دلوقتي — مقدرش أعرض وجهة</span>
      ) : candidates.length === 0 ? (
        <span className="t-meta">مفيش حد تاني نشط في الفريق ينفع تنقلها له</span>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <label className="t-meta" htmlFor={selectId}>
            انقلها لـ
          </label>
          <select
            id={selectId}
            className="desk-field"
            value={targetId ?? ""}
            disabled={locked}
            onChange={(e) => setPicked(e.target.value)}
          >
            {candidates.map((m) => (
              <option key={m.id} value={m.id}>
                {`${m.name_ar} — ${m.open_tasks} مهام`}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={`btn sm ghost${busy ? " is-busy" : ""}`}
            disabled={locked || !target}
            aria-busy={busy}
            onClick={() => target && onReassign(target)}
            title={
              target
                ? `المهمة هتتنقل لـ${target.name_ar} — حِمله دلوقتي ${fmtNum(
                    target.open_tasks,
                  )} مهام`
                : undefined
            }
          >
            {busy ? "بتتنقل…" : "إعادة توزيع"}
          </button>
        </div>
      )}

      <RowError message={error} />
    </div>
  );
}

/** فشل قسم واحد — بيتعرض جوّه القسم بدل ما الشاشة كلها تقع */
function SectionFailure<T>({
  section,
  onRetry,
}: {
  section: Loaded<T> | undefined;
  onRetry: () => void;
}) {
  if (!section || section.ok) return null;
  if (section.offline) return <OfflineState onRetry={onRetry} />;
  return <ErrorState messageAr={section.messageAr} onRetry={onRetry} />;
}

/** رسالة فشل على مستوى الصف — الفعل فشل، والموظف لازم يشوف السبب */
function RowError({ message }: { message: string | undefined }) {
  if (!message) return null;
  return (
    <span className="t-meta" style={{ color: "var(--danger)" }} role="alert">
      {message}
    </span>
  );
}

/* ─────────────────────── أدوات ─────────────────────── */

function matches(query: string, ...fields: (string | null)[]): boolean {
  const q = query.toLowerCase();
  return fields.some((f) => (f ?? "").toLowerCase().includes(q));
}

/** حارس: أي رقم عربي-هندي جاي من السيستم بيتحوّل للاتيني — قاعدة مقفولة */
function latinDigits(text: string): string {
  return text.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));
}
